import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TablePagination,
  IconButton,
  Popover,
  List,
  ListItemButton,
  Checkbox,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Dialog,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { formatDateTimeDisplay } from '../utils/dateBucketFormat';
import {
  formatTableDurationTooltip,
  formatTableDurationValue,
} from '../utils/durationFormat';
import { resolveThresholdBackground, resolveThresholdLabel } from '../utils/colorThresholds';
import { isRemoteImageUrl, isDataImageUrl } from '../utils/localizeImageUrl';
import { applyCalculatedColumns, formatCalculatedDisplay, isCalculatedColumnKey, normalizeCalculatedColumns } from '../utils/calculatedColumns';
import vsaasTheme from '../theme';

/** true si el valor de la celda se ve como una imagen mostrable (URL http(s) o data:image), no cualquier texto. */
function isDisplayableImageValue(value) {
  const str = String(value ?? '').trim();
  if (!str) return false;
  return isRemoteImageUrl(str) || isDataImageUrl(str);
}

function shouldFormatTemporalColumn(column, value, columnDateFormats, columnDateBuckets) {
  if (columnDateFormats?.[column]) return true;
  if (columnDateBuckets?.[column]) return true;
  if (/time|date|timestamp|created|updated|fecha/i.test(column)) return true;
  const s = String(value ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:)/.test(s);
}

const isNumeric = (v) => {
  if (v === null || v === undefined || v === '') return false;
  const n = parseFloat(String(v).trim());
  return !Number.isNaN(n);
};

const compareValues = (a, b, col) => {
  const va = a[col];
  const vb = b[col];
  const na = isNumeric(va) ? parseFloat(String(va).trim()) : null;
  const nb = isNumeric(vb) ? parseFloat(String(vb).trim()) : null;
  if (na != null && nb != null) return na - nb;
  const sa = va !== null && va !== undefined ? String(va).trim().toLowerCase() : '';
  const sb = vb !== null && vb !== undefined ? String(vb).trim().toLowerCase() : '';
  return sa.localeCompare(sb);
};

const ROWS_PER_PAGE_OPTIONS = [50, 100, 250, 500];

const EXPORT_MAX_ROWS = 500;

const DataTable = ({
  data,
  printCapture = false,
  fitContent = false,
  showTotalColumn = false,
  showTotalRow = false,
  visibleColumns = null,
  columnDecimals = {},
  columnDateFormats = {},
  columnDateBuckets = {},
  timeZone,
  timeZoneColumns = null,
  columnDurationFormats = {},
  columnDurationTooltipFormats = {},
  columnColorThresholds = {},
  columnLabels = {},
  columnFilters = {},
  columnImageColumns = {},
  calculatedColumns = [],
  onColumnFilterChange,
  onClearAllFilters,
  showSortHint = true,
}) => {
  const columnHeaderLabel = (column) =>
    columnLabels && columnLabels[column] ? columnLabels[column] : column;
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filterAnchor, setFilterAnchor] = useState({ el: null, column: null });
  const [filterSearch, setFilterSearch] = useState('');
  // URL de la imagen que se esta viendo en grande (null = modal cerrado).
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const hasFilterSupport = typeof onColumnFilterChange === 'function' && !printCapture;
  const hasActiveFilters = hasFilterSupport && Object.keys(columnFilters || {}).some(
    (col) => columnFilters[col] != null && Array.isArray(columnFilters[col])
  );

  // Resetear a la primera página cuando cambian los datos, filtros o el orden
  useEffect(() => {
    setPage(0);
  }, [data, sortByColumn, sortDirection, columnFilters]);

  const handleSort = (column) => {
    if (column === 'Total') return;
    setSortByColumn((prev) => {
      if (prev === column) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return column;
      }
      setSortDirection('asc');
      return column;
    });
  };

  const setSortDirectionTo = (column, direction) => (e) => {
    e.stopPropagation();
    setSortByColumn(column);
    setSortDirection(direction);
  };

  const allColumns = useMemo(
    () => (data && data[0] ? Object.keys(data[0]) : []),
    [data]
  );

  const calcDefs = useMemo(
    () => normalizeCalculatedColumns(calculatedColumns, allColumns),
    [calculatedColumns, allColumns]
  );
  const calcLabelByKey = useMemo(() => {
    const map = {};
    calcDefs.forEach((def) => {
      map[def.key] = def.label;
    });
    return map;
  }, [calcDefs]);
  const headerLabelFor = (column) => calcLabelByKey[column] || columnHeaderLabel(column);

  const tableData = useMemo(
    () => applyCalculatedColumns(data, calcDefs),
    [data, calcDefs]
  );

  const baseColumns = useMemo(() => {
    if (!allColumns.length) return [];
    const pickUnique = (list) => {
      const seen = new Set();
      return list.filter((c) => {
        if (!c || seen.has(c)) return false;
        seen.add(c);
        return true;
      });
    };
    const source = (visibleColumns != null && Array.isArray(visibleColumns) && visibleColumns.length > 0)
      ? pickUnique(visibleColumns.filter((c) => allColumns.includes(c)))
      : pickUnique(allColumns);
    const calcKeys = calcDefs.map((def) => def.key).filter((key) => key && !source.includes(key));
    return [...source, ...calcKeys];
  }, [allColumns, visibleColumns, calcDefs]);

  const filteredData = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];
    if (!hasFilterSupport || !columnFilters || typeof columnFilters !== 'object') return tableData;
    return tableData.filter((row) =>
      baseColumns.every((col) => {
        const allowed = columnFilters[col];
        if (allowed == null || !Array.isArray(allowed)) return true; // sin filtro = mostrar todo
        if (allowed.length === 0) return false; // "deseleccionar todo" = ninguna fila pasa
        const val = row[col];
        const str = val !== null && val !== undefined ? String(val) : '';
        return allowed.includes(str);
      })
    );
  }, [tableData, baseColumns, columnFilters, hasFilterSupport]);

  const distinctByColumn = useMemo(() => {
    const map = {};
    if (!tableData || tableData.length === 0) return map;
    baseColumns.forEach((col) => {
      const set = new Set();
      tableData.forEach((row) => {
        const v = row[col];
        set.add(v !== null && v !== undefined ? String(v) : '');
      });
      map[col] = [...set].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    });
    return map;
  }, [tableData, baseColumns]);

  const sortedData = useMemo(() => {
    if (filteredData.length === 0) return [];
    if (!sortByColumn || !baseColumns.includes(sortByColumn)) return [...filteredData];
    return [...filteredData].sort((a, b) => {
      const cmp = compareValues(a, b, sortByColumn);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortByColumn, sortDirection, baseColumns]);

  const displayRows = useMemo(() => {
    if (sortedData.length === 0) return [];
    if (printCapture) {
      return sortedData.slice(0, EXPORT_MAX_ROWS);
    }
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage, printCapture]);

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">No hay datos para mostrar</Typography>
      </Box>
    );
  }

  const columns = showTotalColumn ? [...baseColumns, 'Total'] : baseColumns;

  const openFilterFor = (column) => (e) => {
    e.stopPropagation();
    setFilterAnchor({ el: e.currentTarget, column });
    setFilterSearch('');
  };
  const closeFilter = () => setFilterAnchor({ el: null, column: null });

  const filterColumn = filterAnchor.column;
  const distinctForFilter = filterColumn ? (distinctByColumn[filterColumn] || []) : [];
  const allowedForFilter = filterColumn && columnFilters?.[filterColumn];
  // undefined/null o no array = sin filtro (todos); [] = ninguno seleccionado; [..] = solo esos
  const effectiveAllowed = (allowedForFilter != null && Array.isArray(allowedForFilter))
    ? allowedForFilter
    : distinctForFilter;
  const filterSearchLower = filterSearch.trim().toLowerCase();
  const filteredDistinct = filterSearchLower
    ? distinctForFilter.filter((v) => String(v).toLowerCase().includes(filterSearchLower))
    : distinctForFilter;

  const handleFilterToggle = (col, value) => {
    if (!onColumnFilterChange) return;
    const distinct = distinctByColumn[col] || [];
    const current = (columnFilters?.[col] != null && Array.isArray(columnFilters[col])) ? columnFilters[col] : distinct;
    const hasValue = current.includes(value);
    const next = hasValue ? current.filter((x) => x !== value) : [...current, value];
    onColumnFilterChange(col, next.length === distinct.length ? [] : next);
  };

  const handleSelectAllFilter = (col) => {
    if (!onColumnFilterChange) return;
    const distinct = distinctByColumn[col] || [];
    const current = (columnFilters?.[col] != null && Array.isArray(columnFilters[col])) ? columnFilters[col] : distinct;
    if (current.length === distinct.length) {
      onColumnFilterChange(col, []); // desmarcar todos
    } else {
      onColumnFilterChange(col, distinct); // marcar todos (quitar filtro)
    }
  };

  // Suma por fila (para columna total)
  const totalSourceColumns = baseColumns.filter(
    (col) => !calcDefs.some((def) => def.key === col)
  );
  const rowTotals = showTotalColumn
    ? displayRows.map((row) =>
        totalSourceColumns.reduce((sum, col) => {
          const v = row[col];
          return isNumeric(v) ? sum + parseFloat(String(v).trim()) : sum;
        }, 0)
      )
    : [];

  // Suma por columna (para fila total)
  const colTotals = showTotalRow
    ? baseColumns.map((col) =>
        displayRows.reduce((sum, row) => {
          const v = row[col];
          return isNumeric(v) ? sum + parseFloat(String(v).trim()) : sum;
        }, 0)
      )
    : [];
  const grandTotal = showTotalColumn && showTotalRow
    ? rowTotals.reduce((a, b) => a + b, 0)
    : 0;

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) return '';
    const calcDef = calcDefs.find((def) => def.key === column);
    if (calcDef && calcDef.op !== 'concat') return formatCalculatedDisplay(value, calcDef);
    const durationFormatted = formatTableDurationValue(
      value,
      column,
      columnDurationFormats,
      columnDurationTooltipFormats
    );
    if (durationFormatted != null) return durationFormatted;
    if (shouldFormatTemporalColumn(column, value, columnDateFormats, columnDateBuckets)) {
      const temporal = formatDateTimeDisplay(value, {
        dateDisplayFormat: columnDateFormats[column],
        dateBucket: columnDateBuckets[column],
        timeZone: !timeZoneColumns || timeZoneColumns.includes(column) ? timeZone : undefined,
      });
      if (temporal != null) return temporal;
    }
    const decimals = columnDecimals && columnDecimals[column];
    if (decimals !== undefined && decimals !== null && decimals !== '') {
      const n = parseFloat(String(value));
      if (!Number.isNaN(n)) return n.toFixed(Number(decimals));
    }
    return String(value);
  };

  const cellHoverTitle = (value, column) => {
    if (value === null || value === undefined) return undefined;
    return formatTableDurationTooltip(
      value,
      column,
      columnDurationFormats,
      columnDurationTooltipFormats
    );
  };

  const headerStyle = {
    fontWeight: 'bold',
    backgroundColor: vsaasTheme.background.default,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '6px 8px',
  };
  const totalCellStyle = {
    ...headerStyle,
    backgroundColor: '#e8e8e8',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: fitContent ? 'auto' : '100%', minWidth: 0, minHeight: 0 }}>
      {hasFilterSupport && hasActiveFilters && onClearAllFilters && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, flexShrink: 0 }}>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={onClearAllFilters}
          >
            Limpiar filtros
          </Button>
          <Typography variant="caption" color="text.secondary">
            {filteredData.length} de {data.length} registros
          </Typography>
        </Box>
      )}
    <TableContainer
      component={Paper}
      sx={{
        flex: fitContent ? '0 0 auto' : 1,
        overflow: printCapture || fitContent ? 'hidden' : 'auto',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
    >
      <Table 
        stickyHeader 
        size="small"
        sx={{
          width: '100%',
          maxWidth: '100%',
          tableLayout: printCapture ? 'fixed' : 'auto',
          '& .MuiTableCell-root': {
            padding: printCapture ? '3px 5px' : '4px 6px',
            whiteSpace: 'nowrap',
            fontSize: printCapture ? '0.8125rem' : '0.875rem',
            ...(printCapture
              ? {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 0,
                }
              : {}),
          },
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const isSortable = !printCapture && column !== 'Total';
              const isSorted = sortByColumn === column;
              const hasColFilter = hasFilterSupport && column !== 'Total' && (columnFilters?.[column] != null && Array.isArray(columnFilters[column]));
              const headerLabel = headerLabelFor(column);
              const calculated = isCalculatedColumnKey(column);
              return (
                <TableCell 
                  key={column} 
                  title={printCapture ? headerLabel : isSortable ? 'Clic para ordenar por esta columna' : headerLabel}
                  sx={{
                    ...(column === 'Total' ? totalCellStyle : headerStyle),
                    ...(isSortable && { cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: '#e8e8e8' } }),
                  }}
                  onClick={() => isSortable && handleSort(column)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', minWidth: 0 }}>
                    {calculated && (
                      <Chip size="small" label="Calculada" sx={{ height: 18, fontSize: 10 }} />
                    )}
                    {headerLabel}
                    {hasFilterSupport && isSortable && (
                      <IconButton
                        size="small"
                        onClick={openFilterFor(column)}
                        sx={{
                          p: 0.25,
                          color: hasColFilter ? 'primary.main' : 'action.disabled',
                          '&:hover': { color: 'primary.main' },
                        }}
                        title="Filtrar columna"
                      >
                        {hasColFilter ? <FilterListIcon fontSize="small" /> : <FilterListOffIcon fontSize="small" />}
                      </IconButton>
                    )}
                    {isSortable && isSorted && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        <ArrowUpwardIcon
                          onClick={setSortDirectionTo(column, 'asc')}
                          sx={{
                            fontSize: 18,
                            cursor: 'pointer',
                            color: sortDirection === 'asc' ? 'primary.main' : 'action.disabled',
                            '&:hover': { color: 'primary.main' },
                          }}
                          title="Ascendente"
                        />
                        <ArrowDownwardIcon
                          onClick={setSortDirectionTo(column, 'desc')}
                          sx={{
                            fontSize: 18,
                            cursor: 'pointer',
                            color: sortDirection === 'desc' ? 'primary.main' : 'action.disabled',
                            '&:hover': { color: 'primary.main' },
                          }}
                          title="Descendente (inversa)"
                        />
                      </Box>
                    )}
                    {isSortable && !isSorted && showSortHint && (
                      <Box component="span" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>(ordenar)</Box>
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows.map((row, index) => (
            <TableRow key={index}>
              {baseColumns.map((column) => {
                const cellValue = formatCellValue(row[column], column);
                const hover = cellHoverTitle(row[column], column);
                const cellBg = resolveThresholdBackground(row[column], columnColorThresholds[column]);
                const cellLabel = resolveThresholdLabel(row[column], columnColorThresholds[column]);
                const cellText = cellLabel ? `${cellValue} (${cellLabel})` : cellValue;
                const showAsImage = columnImageColumns?.[column] && isDisplayableImageValue(row[column]);
                return (
                  <TableCell
                    key={column}
                    title={showAsImage ? undefined : hover || cellText}
                    onClick={showAsImage ? () => setPreviewImageUrl(row[column]) : undefined}
                    sx={{
                      padding: '4px 6px',
                      ...(cellBg ? { backgroundColor: cellBg } : {}),
                      ...(showAsImage ? { cursor: 'pointer' } : {}),
                    }}
                  >
                    {showAsImage ? (
                      <Box
                        component="img"
                        src={row[column]}
                        alt=""
                        loading="lazy"
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 0.5,
                          display: 'block',
                          pointerEvents: 'none',
                        }}
                      />
                    ) : (
                      cellText
                    )}
                  </TableCell>
                );
              })}
              {showTotalColumn && (
                <TableCell sx={{ padding: '4px 6px', fontWeight: 500 }}>
                  {rowTotals[index] != null
                    ? Number(rowTotals[index]).toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : ''}
                </TableCell>
              )}
            </TableRow>
          ))}
          {showTotalRow && (
            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.06)' }}>
              <TableCell sx={{ ...totalCellStyle, position: 'sticky', left: 0, zIndex: 1 }}>
                Total
              </TableCell>
              {baseColumns.slice(1).map((column, colIndex) => {
                const total = colTotals[colIndex + 1];
                const totalCalc = calcDefs.find((def) => def.key === column);
                const totalText = total == null
                  ? ''
                  : totalCalc && totalCalc.op !== 'concat'
                    ? formatCalculatedDisplay(total, totalCalc)
                    : Number(total).toLocaleString(undefined, { maximumFractionDigits: 2 });
                return (
                  <TableCell key={column} sx={{ padding: '4px 6px', fontWeight: 600 }}>
                    {totalText}
                  </TableCell>
                );
              })}
              {showTotalColumn && (
                <TableCell sx={{ padding: '4px 6px', fontWeight: 600 }}>
                  {Number(grandTotal).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
      {!printCapture && filteredData.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          showFirstButton
          showLastButton
        />
      )}
    </TableContainer>

    <Popover
      open={Boolean(filterAnchor.el && filterColumn)}
      anchorEl={filterAnchor.el}
      onClose={closeFilter}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ minWidth: 220, maxWidth: 320, maxHeight: 360, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            placeholder="Buscar..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="caption" color="text.secondary">Buscar</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List dense sx={{ overflow: 'auto', flex: 1, py: 0 }}>
          <ListItemButton dense onClick={() => handleSelectAllFilter(filterColumn)}>
            <Checkbox
              checked={effectiveAllowed.length === distinctForFilter.length}
              indeterminate={effectiveAllowed.length > 0 && effectiveAllowed.length < distinctForFilter.length}
              disableRipple
              size="small"
            />
            <Typography variant="body2">(Seleccionar todo)</Typography>
          </ListItemButton>
          {filteredDistinct.map((value) => {
            const label = value === '' ? '(vacío)' : String(value);
            const checked = effectiveAllowed.includes(value);
            return (
              <ListItemButton
                key={label}
                dense
                onClick={() => handleFilterToggle(filterColumn, value)}
              >
                <Checkbox checked={checked} disableRipple size="small" />
                <Typography variant="body2" noWrap title={label} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {label}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Popover>

    <Dialog open={Boolean(previewImageUrl)} onClose={() => setPreviewImageUrl(null)} maxWidth="lg">
      <Box sx={{ position: 'relative', lineHeight: 0 }}>
        <IconButton
          size="small"
          onClick={() => setPreviewImageUrl(null)}
          sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {previewImageUrl && (
          <Box component="img" src={previewImageUrl} alt="" sx={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh' }} />
        )}
      </Box>
    </Dialog>
    </Box>
  );
};

export default DataTable;
